/**
 * APIKeyManagementPage — `/integration/api-keys`.
 *
 * Issue, revoke API keys for integration partners via
 * service-identity `/v1/internal/api-keys`.
 *
 * Key issuance returns the plaintext key once — shown in a modal with a
 * copy button and a warning. After dismissal it cannot be retrieved.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { Ban, Copy, Key, Plus, X } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface ApiKeyRow {
  id: string;
  serviceAccountId: string;
  accountName: string | null;
  prefix: string;
  lastFour: string;
  status: 'ACTIVE' | 'REVOKED' | 'EXPIRED';
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

interface IssuedKey {
  keyId: string;
  serviceAccountId: string;
  accountName: string;
  plaintextKey: string;
  prefix: string;
  expiresAt: string | null;
  warning: string;
}

export function APIKeyManagementPage(): JSX.Element {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issuedKey, setIssuedKey] = useState<IssuedKey | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('365');
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request<{ keys: ApiKeyRow[] }>(
        env.identityBaseUrl,
        '/v1/internal/api-keys',
      );
      setKeys(data.keys);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadKeys(); }, [loadKeys]);

  async function handleIssue(): Promise<void> {
    if (!accountName.trim()) return;
    setIssuing(true);
    try {
      const data = await request<IssuedKey>(
        env.identityBaseUrl,
        '/v1/internal/api-keys',
        {
          method: 'POST',
          body: {
            accountName: accountName.trim(),
            expiresInDays: parseInt(expiresInDays, 10) || 365,
          },
        },
      );
      setIssuedKey(data);
      setShowIssueModal(false);
      setAccountName('');
      await loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to issue key');
    } finally {
      setIssuing(false);
    }
  }

  async function handleRevoke(id: string): Promise<void> {
    if (!window.confirm('Revoke this API key? This action is permanent.')) return;
    try {
      await request<unknown>(
        env.identityBaseUrl,
        `/v1/internal/api-keys/${id}/revoke`,
        { method: 'POST' },
      );
      await loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke key');
    }
  }

  async function handleCopy(): Promise<void> {
    if (!issuedKey) return;
    await navigator.clipboard.writeText(issuedKey.plaintextKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const activeCount = keys.filter((k) => k.status === 'ACTIVE').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="API key management"
        description="Issue and revoke API keys for integration partners."
        actions={
          <button
            type="button"
            onClick={() => setShowIssueModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            data-testid="open-issue-key-modal"
          >
            <Plus className="w-4 h-4" /> Issue key
          </button>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-3 underline">dismiss</button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-nipost-green" />
            <p className="text-xs font-medium text-muted">Total keys</p>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="total-keys-count">
            {loading ? '…' : keys.length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-green-500" />
            <p className="text-xs font-medium text-muted">Active</p>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="active-keys-count">
            {loading ? '…' : activeCount}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted text-sm">Loading API keys…</div>
        ) : keys.length === 0 ? (
          <div className="p-8 text-center text-muted text-sm" data-testid="no-keys-notice">
            No API keys issued yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Account</th>
                <th className="p-3 text-left">Prefix</th>
                <th className="p-3 text-left">Last 4</th>
                <th className="p-3 text-left">Expires</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-t border-border hover:bg-gray-50" data-testid="api-key-row">
                  <td className="p-3 font-medium text-gray-900">{k.accountName ?? '—'}</td>
                  <td className="p-3 font-mono text-xs">upmp_pk_{k.prefix}_…</td>
                  <td className="p-3 font-mono text-xs">…{k.lastFour}</td>
                  <td className="p-3 text-xs">
                    {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-3 text-center"><StatusBadge status={k.status} /></td>
                  <td className="p-3 text-right">
                    {k.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        onClick={() => void handleRevoke(k.id)}
                        className="px-2 py-1 text-xs font-medium text-danger rounded inline-flex items-center gap-1 hover:bg-red-50"
                        data-testid="revoke-key-btn"
                      >
                        <Ban className="w-3 h-3" /> Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Issue key modal */}
      {showIssueModal ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">Issue API key</p>
              <button
                type="button"
                onClick={() => setShowIssueModal(false)}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted mb-1" htmlFor="account-name">
                  Service account name
                </label>
                <input
                  id="account-name"
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. Paystack Production"
                  className="w-full text-sm px-3 py-2 border border-border rounded-lg"
                  data-testid="issue-key-account-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1" htmlFor="expires-in">
                  Expires in (days)
                </label>
                <input
                  id="expires-in"
                  type="number"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  min="1"
                  max="365"
                  className="w-full text-sm px-3 py-2 border border-border rounded-lg"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setShowIssueModal(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleIssue()}
                disabled={issuing || !accountName.trim()}
                className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-50"
                data-testid="issue-key-submit"
              >
                {issuing ? 'Issuing…' : 'Issue key'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Plaintext key reveal modal — shown once after issuance */}
      {issuedKey ? (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">API key issued</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                {issuedKey.warning}
              </div>
              <div>
                <p className="text-xs font-medium text-muted mb-1">Account</p>
                <p className="text-sm font-medium">{issuedKey.accountName}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted mb-1">Plaintext key (shown once)</p>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 bg-gray-50 border border-border rounded px-3 py-2 text-xs font-mono break-all"
                    data-testid="issued-key-plaintext"
                  >
                    {issuedKey.plaintextKey}
                  </code>
                  <button
                    type="button"
                    onClick={() => void handleCopy()}
                    className="shrink-0 p-2 rounded hover:bg-gray-100"
                    title="Copy to clipboard"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {copied ? <p className="text-xs text-green-600 mt-1">Copied!</p> : null}
              </div>
            </div>
            <div className="flex items-center justify-end p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setIssuedKey(null)}
                className="px-4 py-2 text-sm bg-nipost-green text-white rounded-lg"
                data-testid="dismiss-issued-key"
              >
                I have saved the key
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
